import "./assets/index.css";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useInvestigationAccidentCommand } from "./hooks/useInvestigationAccidentCommand";
import { useReportAccidentQuery } from "../reportRoadAccidents/hooks/useReportAccidentQuery";
import { ROUTES } from "../../core/routes/routes";
import Textfield from "../../common/components/Textfiled";
import Select from "../../common/components/Select";
import SelectSearch from "../../common/components/SelectSearch";
import {
  OPTIONS_AFFECTED_BODY_PART,
  OPTIONS_BEHAVIOR_OR_CONDITION,
  OPTIONS_INCIDENT_MECHANISM,
  OPTIONS_INJURY_AGENT,
  OPTIONS_INJURY_TYPE,
} from "./constants/constants";
import Button from "../../common/components/Button";
import { useRef } from "react";

function RegisterInvestigation() {
  const location = useLocation();
  const navigateTo = useNavigate();
  const editData = location.state?.editData || null;
  const isEditing = !!editData;

  const {
    investigation,
    handleChangeInvestigation,
    errors,
    createInvestigationAccident,
    updateInvestigationAccident,
    setInvestigation,
  } = useInvestigationAccidentCommand(() => {
    if (isEditing) navigateTo(ROUTES.LIST_INVESTIGATION);
  });
  const [search, setSearch] = useState(investigation.accident || "");
  const { reportAccidents } = useReportAccidentQuery({ search, limit: 10, ...(isEditing ? {} : { excludeWithInvestigation: true }) });
  const refInputEventDetails = useRef(null);

  useEffect(() => {
    if (editData) {
      setInvestigation({
        id: editData.id,
        accident: editData.accident || "",
        committeeAnalysisDate: editData.committeeAnalysisDate ? editData.committeeAnalysisDate.split("T")[0] : "",
        directFatalityCosts: editData.directFatalityCosts || "",
        indirectFatalityCosts: editData.indirectFatalityCosts || "",
        directSevereInjuryCosts: editData.directSevereInjuryCosts || "",
        indirectSevereInjuryCosts: editData.indirectSevereInjuryCosts || "",
        directMinorInjuryCosts: editData.directMinorInjuryCosts || "",
        indirectMinorInjuryCosts: editData.indirectMinorInjuryCosts || "",
        directSimpleCollisionCosts: editData.directSimpleCollisionCosts || "",
        indirectSimpleCollisionCosts: editData.indirectSimpleCollisionCosts || "",
        accidentPaymentProof: editData.accidentPaymentProof || "",
        injuryAgent: editData.injuryAgent || "",
        behaviorOrCondition: editData.behaviorOrCondition || "",
        incidentMechanism: editData.incidentMechanism || "",
        disabilityDays: editData.disabilityDays || "",
        injuryType: editData.injuryType || "",
        affectedBodyPart: editData.affectedBodyPart || "",
        correctiveAction: editData.correctiveAction || "",
        actionResponsible: editData.actionResponsible || "",
        actionExecutionDate: editData.actionExecutionDate ? editData.actionExecutionDate.split("T")[0] : "",
        eventDetails: editData.eventDetails || null,
      });
    }
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (isEditing) {
      updateInvestigationAccident({ ...investigation, id: editData.id });
    } else {
      createInvestigationAccident(investigation).then(() => {
        refInputEventDetails.current.value = "";
      });
    }
  };

  return (
    <main className="container">
      <header className="header-container">
        <h1 className="header-title">{isEditing ? "Editar investigación de siniestros viales" : "Registrar reporte de siniestros viales"}</h1>
      </header>
      <section className="section-container">
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <section className="border border-dark rounded flex-column">
              <header className="bg-dark p-3 text-white">
                <h2 className="fs-4">Selecciona un siniestro</h2>
              </header>
              <div className="p-3">
                <SelectSearch
                  name="accident"
                  label="Siniestros"
                  placeholder="Selecciona un siniestro"
                  options={reportAccidents.map((accident) => ({
                    value: accident.id,
                    label: `${accident.dateAccident} - ${accident.id} - ${accident.namePersonInvolved} - ${accident.vehiclePlate}`,
                  }))}
                  value={investigation.accident}
                  onSelect={handleChangeInvestigation}
                  onSearch={(text) => setSearch(text)}
                  error={errors.errors.accident}
                />
              </div>
            </section>
          </div>
          <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 mb-3 g-3">
            <Textfield
              label="Fecha del comite donde fue analizado"
              type="date"
              placeholder="Fecha del comite donde fue analizado"
              name="committeeAnalysisDate"
              value={investigation.committeeAnalysisDate}
              onChange={handleChangeInvestigation}
              error={errors.errors.committeeAnalysisDate}
            />
            <Textfield
              label="Costos directos de fatalidades"
              type="number"
              placeholder="Costos directos de fatalidades"
              name="directFatalityCosts"
              value={investigation.directFatalityCosts}
              onChange={handleChangeInvestigation}
              error={errors.errors.directFatalityCosts}
            />
            <Textfield
              label="Costos indirectos de fatalidades"
              type="number"
              placeholder="Costos indirectos de fatalidades"
              name="indirectFatalityCosts"
              value={investigation.indirectFatalityCosts}
              onChange={handleChangeInvestigation}
              error={errors.errors.indirectFatalityCosts}
            />

            <Textfield
              label="Costos directos de heridos graves"
              type="number"
              placeholder="Costos directos de heridos graves"
              name="directSevereInjuryCosts"
              value={investigation.directSevereInjuryCosts}
              onChange={handleChangeInvestigation}
              error={errors.errors.directSevereInjuryCosts}
            />

            <Textfield
              label="Costos indirectos de heridos graves"
              type="number"
              placeholder="Costos indirectos de heridos graves"
              name="indirectSevereInjuryCosts"
              value={investigation.indirectSevereInjuryCosts}
              onChange={handleChangeInvestigation}
              error={errors.errors.indirectSevereInjuryCosts}
            />

            <Textfield
              label="Costos directos de heridos leves"
              type="number"
              placeholder="Costos directos de heridos leves"
              name="directMinorInjuryCosts"
              value={investigation.directMinorInjuryCosts}
              onChange={handleChangeInvestigation}
              error={errors.errors.directMinorInjuryCosts}
            />

            <Textfield
              label="Costos indirectos de heridos leves"
              type="number"
              placeholder="Costos indirectos de heridos leves"
              name="indirectMinorInjuryCosts"
              value={investigation.indirectMinorInjuryCosts}
              onChange={handleChangeInvestigation}
              error={errors.errors.indirectMinorInjuryCosts}
            />

            <Textfield
              label="Costos directos de choques simples"
              type="number"
              placeholder="Costos directos de choques simples"
              name="directSimpleCollisionCosts"
              value={investigation.directSimpleCollisionCosts}
              onChange={handleChangeInvestigation}
              error={errors.errors.directSimpleCollisionCosts}
            />

            <Textfield
              label="Costos indirectos de choques simples"
              type="number"
              placeholder="Costos indirectos de choques simples"
              name="indirectSimpleCollisionCosts"
              value={investigation.indirectSimpleCollisionCosts}
              onChange={handleChangeInvestigation}
              error={errors.errors.indirectSimpleCollisionCosts}
            />

            <Textfield
              label="Comprobante de egreso o pago a tercero por concepto de accidente"
              type="text"
              placeholder="Comprobante de pago de accidente"
              name="accidentPaymentProof"
              value={investigation.accidentPaymentProof}
              onChange={handleChangeInvestigation}
              error={errors.errors.accidentPaymentProof}
            />

            <Select
              label="Agente de lesión"
              name="injuryAgent"
              value={investigation.injuryAgent}
              onChange={handleChangeInvestigation}
              error={errors.errors.injuryAgent}
              options={OPTIONS_INJURY_AGENT}
            />

            <Select
              label="Comportamiento o condición"
              name="behaviorOrCondition"
              value={investigation.behaviorOrCondition}
              onChange={handleChangeInvestigation}
              error={errors.errors.behaviorOrCondition}
              options={OPTIONS_BEHAVIOR_OR_CONDITION}
            />

            <Select
              label="Mecanismo de siniestros"
              name="incidentMechanism"
              value={investigation.incidentMechanism}
              onChange={handleChangeInvestigation}
              error={errors.errors.incidentMechanism}
              options={OPTIONS_INCIDENT_MECHANISM}
            />

            <Textfield
              label="Días de discapacidad"
              type="number"
              placeholder="Días de discapacidad"
              name="disabilityDays"
              value={investigation.disabilityDays}
              onChange={handleChangeInvestigation}
              error={errors.errors.disabilityDays}
            />

            <Select
              label="Tipo de lesión"
              name="injuryType"
              value={investigation.injuryType}
              onChange={handleChangeInvestigation}
              error={errors.errors.injuryType}
              options={OPTIONS_INJURY_TYPE}
            />

            <Select
              label="Parte del cuerpo afectada"
              name="affectedBodyPart"
              value={investigation.affectedBodyPart}
              onChange={handleChangeInvestigation}
              error={errors.errors.affectedBodyPart}
              options={OPTIONS_AFFECTED_BODY_PART}
            />

            <Textfield
              label="Acción correctiva"
              type="text"
              placeholder="Acción correctiva"
              name="correctiveAction"
              value={investigation.correctiveAction}
              onChange={handleChangeInvestigation}
              error={errors.errors.correctiveAction}
            />

            <Textfield
              label="Responsable de la acción"
              type="text"
              placeholder="Responsable de la acción"
              name="actionResponsible"
              value={investigation.actionResponsible}
              onChange={handleChangeInvestigation}
              error={errors.errors.actionResponsible}
            />

            <Textfield
              label="Fecha de ejecución de la acción"
              type="date"
              placeholder="Fecha de ejecución de la acción"
              name="actionExecutionDate"
              value={investigation.actionExecutionDate}
              onChange={handleChangeInvestigation}
              error={errors.errors.actionExecutionDate}
            />

            <Textfield
              label="Detalles del evento"
              type="file"
              placeholder="Detalles del evento"
              name="eventDetails"
              reference={refInputEventDetails}
              onChange={({ name, file }) =>
                handleChangeInvestigation({ name, value: file })
              }
              error={errors.errors.eventDetails}
            />
          </div>

          <footer className="form-footer">
            <Button type="submit">{isEditing ? "Actualizar" : "Registrar"}</Button>
          </footer>
        </form>
      </section>
    </main>
  );
}

export default RegisterInvestigation;
